/*
 * Copyright (c) 2022 Honeywell International, Inc. All rights reserved.
 *
 * This file contains trade secrets of Honeywell International, Inc.  No part
 *  may be reproduced or transmitted in any form by any means or for any
 *  purpose without the express written permission of Honeywell.
 *
 */

import './DIYHomePage.scss'
import React, { useEffect, useState } from "react";
import { Button, Card, Grid, Loader, Radio } from "@scuf/common";
import { routerFn } from "../../routerInstance";
import { FCPDIYGatewayManagementHeader } from './FCPDIYGatewayManagementHeader';
import { ILoggedInUserDetails } from '../../stores/GatewayManager/IGatewayRegistrationInterfaces';
import featureFlagStore from '../../utils/FeatureFlags/APFeatureFlagsStore';
import adminPortalImg from "../../../src/images/admin-portal.png";
import forgeBuilderImg from "../../../src/images/forge-builder.png";
import GlobalStore from "../../stores/GlobalStore/GlobalStore";
import { inject } from 'mobx-react';
import './../../components/layout.scss';
import { parse } from 'query-string';
import DIYAppConstants from '../../utils/DIYAppConstants';


export interface IDIYHomePageProps {
    globalStore: GlobalStore;
}

/**
 *  This is the new landing page for the DIY application. It allows user
 *  to chose between admin portal based and epm flows.
 */
export const DIYHomePageInternal = (props: IDIYHomePageProps) => {
    const [selected, setSelected] = useState('ap');
    const [featureFlagInitialized, setFlagInitialized] = useState(false);

    const parsedQueryString = parse(window.location.search);
    let redirect_uri = parsedQueryString.redirect_uri?.toString()?.replace(/\/$/, "");
    let domain = parsedQueryString.domain;


    useEffect(() => {
        featureFlagStore.init(() => setFlagInitialized(true));
        if(domain === 'epm') setSelected('fp');
    }, []);

    const navigateToDIY = () => {
        const qp = getQueryParameters();
        if (selected === 'fp') {
            routerFn().navigate("fcp-register", {
                action: 'main',
                ...qp
            });
        } else {
            routerFn().navigate("ap-register", qp);
        }
    }

    const navigateToDevice = () => {
        let forge_route : string;
        if( selected === 'fp'){
            domain = 'epm';
            forge_route = DIYAppConstants.EPM_ROUTE;
        } else {
            domain = 'non-epm';
            forge_route = DIYAppConstants.NON_EPM_ROUTE;

        }
        const deviceUrl = DIYAppConstants.DEVICE_URL(redirect_uri! , domain, forge_route);
        window.location.assign(deviceUrl);
    }

    const getQueryParameters = (): any => {
        const parsedQueryString = parse(window.location.search);
        let sn = parsedQueryString.deviceSerialNumber,
            epName = parsedQueryString.originalEp,
            enrId = parsedQueryString.enrollmentId;
        return {
            deviceSerialNumber: sn,
            originalEp: epName,
            enrollmentId: enrId
        };
    };

    const getAppLoadingMessage = () => {
        return (
            <React.Fragment>
                <div className='application-container portal-selection-container'>
                    <div className='application-main-content'>
                        <div className={`application-page-content ui-portal-selection-container `}>
                            <Loader loading={true} text='Initializing Application'></Loader>
                        </div>
                    </div>
                </div>
            </React.Fragment>
        );
    }

    const createPortalList = () => {
        let apSupportEmail = props.globalStore!.envVariables.AP_ONBOARDING_FROM_EMAIL;
        let fpSupporEmail = props.globalStore!.envVariables.FP_ONBOARDING_FROM_EMAIL;
        return <React.Fragment>
            <div className='application-container portal-selection-container'>
                <div className='application-main-content'>

                    <div className={`application-page-content ui-portal-selection-container `}>
                        <div className={'ui-decision-page-title'}>
                            Register Via
                        </div>
                        <div className='ui-decision-page-msg'>
                            Which is the forge portal you wish to proceed with:
                        </div>
                        <div className='portal-selection-details'>
                            <Grid>
                                <Grid.Row className={'ui-portal-block-selected'}
                                    onClick={() => { setSelected('ap') }}>

                                    <Grid.Column xsWidth={1} width={1} >
                                        <div className='ui-portal-select-radio'>
                                            <Radio checked={selected === 'ap'} />
                                        </div>
                                    </Grid.Column>
                                    <Grid.Column xsWidth={11} width={7} >
                                        <div className='ui-portal-block-details'>
                                            <div className='ui-portal-details-title'>
                                                Admin portal
                                            </div>

                                            <div className='ui-portal-details-desc'>
                                                if you have an email from <span className='ui-portal-email'>{apSupportEmail}</span>
                                            </div>
                                        </div>
                                    </Grid.Column>
                                    <Grid.Column xsWidth={12} width={4} >
                                        <div className='portal-image-container'>
                                            <img src={adminPortalImg} className='portal-image' />
                                        </div>
                                    </Grid.Column>
                                </Grid.Row>

                                <Grid.Row className={'ui-portal-block-unselected'}
                                    onClick={() => { setSelected('fp') }}>

                                    <Grid.Column width={1} xsWidth={1}>
                                        <div className='ui-portal-select-radio'>
                                            <Radio checked={selected === 'fp'} />
                                        </div>
                                    </Grid.Column>
                                    <Grid.Column width={7} xsWidth={11}>
                                        <div className='ui-portal-block-details'>
                                            <div className='ui-portal-details-title'>
                                                Forge Builder (Self Service Portal)
                                            </div>
                                            <div className='ui-portal-details-desc'>
                                                if you have an email from <span className='ui-portal-email'>{fpSupporEmail}</span>
                                            </div>
                                        </div>
                                    </Grid.Column>
                                    <Grid.Column width={4} xsWidth={12}>
                                        <div className='portal-image-container'>
                                            <img src={forgeBuilderImg} className='portal-image' />
                                        </div>
                                    </Grid.Column>
                                </Grid.Row>
                            </Grid>
                        </div>
                    </div>
                    <div className="application-navigation ui-box-shadow">
                        <div className="ui-site-selection-navigation">
                            <Button content={redirect_uri?' Test Connectivity' : 'Proceed'}
                                type='primary'
                                onClick={() => redirect_uri? navigateToDevice() : navigateToDIY()}></Button>
                        </div>
                    </div>

                </div>
            </div>
        </React.Fragment>;
    }

    const displayPortalList = () => {
        if (featureFlagInitialized) {
            if (redirect_uri || featureFlagStore.getForgeEPMEnabled) {
                return createPortalList();
            }
            else {
                const qp = getQueryParameters();
                routerFn().navigate("ap-register", qp);
                return <div />
            }
        } else {
            return getAppLoadingMessage();
        }
    }

    return <React.Fragment>
        <FCPDIYGatewayManagementHeader logout={undefined} userInfo={{} as ILoggedInUserDetails} />
        {displayPortalList()}
    </React.Fragment>

}

export const DIYHomePage = inject('globalStore')(DIYHomePageInternal);
